import {useCallback, useEffect, useMemo, useState} from 'react';
import useScreenSize from '../../hooks/useScreenSize';
import {useSelector} from 'react-redux';
import {getMovementSummary} from '../../api/moneyMovement';
import {getAccountsTimelines} from '../../api/ledger';
import {selectAccountsSorted, selectProjectsSorted} from '../../store/selectors';
import {selectCurrencyRates} from '../../store/currencyRates';
import Loader from '../../components/common/Loader';
import TransactionFilter from '../../components/common/TransactionFilter';
import {
    aggregateBalances,
    summariesToEventData,
    buildBalanceChartData,
    buildIncomeExpenseChartData,
    computeMonthlyRates,
    getActiveCurrencies,
    buildMonthsFromSummaries,
} from './timelineUtils';
import TimelineBarChart from './components/TimelineBarChart';
import TimelineLineChart from './components/TimelineLineChart';
import TimelineTable from './components/TimelineTable';
import './TimelinePage.scss';
import TransactionFilterButton from '../../components/common/TransactionFilter/TransactionFilterButton';

const TimelinePage = () => {
    const accounts = useSelector(selectAccountsSorted);
    const currencyRates = useSelector(selectCurrencyRates);
    const projects = useSelector(selectProjectsSorted);

    const [view, setView] = useState('chart');
    const [currencyMode, setCurrencyMode] = useState('ALL');
    const [months, setMonths] = useState([]);
    const [eventData, setEventData] = useState({});
    const [balanceData, setBalanceData] = useState({});
    const [monthlyRates, setMonthlyRates] = useState({UAH: []});
    const [loading, setLoading] = useState(false);
    const [filters, setFilters] = useState(null);
    const {isSidebarOpen} = useScreenSize();
    const [filterOpen, setFilterOpen] = useState(isSidebarOpen);

    const onFiltersChange = useCallback(f => setFilters(f), []);

    useEffect(() => {
        if (!filters) return;

        const {from, to, accountIds, types, searchText} = filters;
        const statsAccounts = accounts
            .filter(a => a.includeInStats)
            .filter(a => !accountIds?.length || accountIds.includes(a.id));

        setLoading(true);
        const statsAccountIds = statsAccounts.map(a => a.id);

        Promise.all([
            getMovementSummary({from, to, accountIds, projectIds: filters.projectIds, types, searchText}),
            statsAccountIds.length
                ? getAccountsTimelines(statsAccountIds, from, to)
                : Promise.resolve([]),
        ])
            .then(([summaries, timelines]) => {
                const mos = buildMonthsFromSummaries(summaries);
                setMonths(mos);
                setEventData(summariesToEventData(summaries, mos));
                setBalanceData(aggregateBalances(timelines, mos));
                setMonthlyRates(computeMonthlyRates(mos, currencyRates));
            })
            .finally(() => setLoading(false));
    }, [filters, accounts, currencyRates]); // eslint-disable-line react-hooks/exhaustive-deps

    const currencies = useMemo(
        () => getActiveCurrencies(eventData, balanceData),
        [eventData, balanceData]
    );

    useEffect(() => {
        if (currencyMode !== 'ALL' && !currencies.includes(currencyMode)) {
            setCurrencyMode('ALL');
        }
    }, [currencies, currencyMode]);

    const incomeExpenseData = useMemo(
        () => buildIncomeExpenseChartData(currencyMode, months, eventData, monthlyRates),
        [currencyMode, months, eventData, monthlyRates]
    );
    const balData = useMemo(
        () => buildBalanceChartData(currencyMode, months, balanceData, monthlyRates),
        [currencyMode, months, balanceData, monthlyRates]
    );

    const yAxisLabel = currencyMode === 'ALL' ? 'UAH' : currencyMode;
    const tablePreset = months.length > 13 ? 'all-time' : 'last-year';

    return (
        <div className="capital-page timeline-page">
            <div className="capital-page__header">
                <TransactionFilterButton onClick={() => setFilterOpen(v => !v)}/>
                <h2 className="capital-page__title">Timeline</h2>
                <div className="timeline-page__controls">
                    <div className="timeline-view-switch">
                        <button className={`timeline-switch-btn${view === 'chart' ? ' timeline-switch-btn--active' : ''}`} onClick={() => setView('chart')}>Chart</button>
                        <button className={`timeline-switch-btn${view === 'table' ? ' timeline-switch-btn--active' : ''}`} onClick={() => setView('table')}>Table</button>
                    </div>
                </div>
            </div>

            <div className="capital-page__body">
                <div className="timeline-layout">
                    <TransactionFilter
                        accounts={accounts}
                        projects={projects}
                        onFiltersChange={onFiltersChange}
                        expanded={filterOpen}
                        onExpandedChange={setFilterOpen}
                    />

                    <div className="timeline-content">
                        {loading ? (
                            <Loader/>
                        ) : !currencies.length ? (
                            <p className="capital-page__placeholder">No data for this period.</p>
                        ) : view === 'chart' ? (
                            <>
                                <TimelineBarChart
                                    data={incomeExpenseData}
                                    label={`Income & Expenses · ${yAxisLabel}`}
                                    height={240}
                                />
                                <TimelineLineChart
                                    data={balData}
                                    label={`Balance · ${yAxisLabel}`}
                                    height={220}
                                />
                            </>
                        ) : (
                            <TimelineTable
                                months={months}
                                eventData={eventData}
                                monthlyRates={monthlyRates}
                                currencyMode={currencyMode}
                                preset={tablePreset}
                                currencies={currencies}
                            />
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default TimelinePage;